import {
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { WidgetOrchestratorContext, useWidgetStore } from '../../state/createWidgetStore';
import { getSttService } from '../../services/stt';
import { ensureOrchestratorRef } from '../messageList/MessageList.helpers';
import { isSsoProvider } from './helpers';
import { useRecordingWaveform } from './useRecordingWaveform';
import { useRateLimitGate } from './useRateLimitGate';
import { useInputBarActions } from './useInputBarActions';
import { RateLimitCountdownView, CaptchaChallengeView, CaptchaLoadingView } from './RateLimitViews';
import { SsoLoginView, SsoPendingView } from './SsoViews';
import { ChatComposer } from './ChatComposer';

interface InputBarProps {
  shouldFocus?: boolean;
  portalMode?: boolean;
}

export function InputBar({ shouldFocus = false, portalMode = false }: InputBarProps) {
  const orchestratorRef = useContext(WidgetOrchestratorContext);

  const isLoading = useWidgetStore((s) => s.isLoading);
  const auth = useWidgetStore((s) => s.auth);
  const config = useWidgetStore((s) => s.config);
  const rateLimitLock = useWidgetStore((s) => s.rateLimitLock);
  const pendingAttachments = useWidgetStore((s) => s.pendingAttachments);
  const fileUpload = useWidgetStore((s) => s.fileUpload);
  const conversationId = useWidgetStore((s) => s.conversationId);
  const clientChatId = useWidgetStore((s) => s.clientChatId);
  const inputFocusPending = useWidgetStore((s) => s.inputFocusPending);
  const drivingListening = useWidgetStore((s) => s.drivingMode.listening);

  const addMessage = useWidgetStore((s) => s.addMessage);
  const removeMessage = useWidgetStore((s) => s.removeMessage);
  const updateMessage = useWidgetStore((s) => s.updateMessage);
  const setState = useWidgetStore((s) => s.setState);
  const setAuth = useWidgetStore((s) => s.setAuth);
  const setError = useWidgetStore((s) => s.setError);
  const setFileUpload = useWidgetStore((s) => s.setFileUpload);
  const addPendingAttachment = useWidgetStore((s) => s.addPendingAttachment);
  const removePendingAttachment = useWidgetStore((s) => s.removePendingAttachment);
  const clearPendingAttachments = useWidgetStore((s) => s.clearPendingAttachments);
  const setDrivingMode = useWidgetStore((s) => s.setDrivingMode);
  const requestInputFocus = useWidgetStore((s) => s.requestInputFocus);
  const consumeInputFocus = useWidgetStore((s) => s.consumeInputFocus);

  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  const isAuthenticated = auth.status === 'authenticated';
  const sttSupported = getSttService().isSupported();
  const showAttach = isAuthenticated && config.uploadEnabled;
  const showMic = isAuthenticated && sttSupported && config.voiceEnabled;
  const showDrivingMode = isAuthenticated && config.voiceEnabled && config.drivingModeEnabled && portalMode;
  const useSso = isSsoProvider(config.authProvider);

  const waveform = useRecordingWaveform(drivingListening);

  const handleDrivingModeClick = useCallback(() => {
    void (async () => {
      setDrivingMode({ processing: true, error: null });
      const orchestrator = await ensureOrchestratorRef();

      if (!orchestrator) {
        setDrivingMode({ processing: false, error: 'sttNetworkError' });
        return;
      }

      setDrivingMode({ active: true, processing: false, error: null });
    })();
  }, [setDrivingMode]);

  const actions = useInputBarActions({
    orchestratorRef,
    auth,
    config,
    isLoading,
    pendingAttachments,
    conversationId,
    clientChatId,
    inputRef,
    drivingListening,
    addMessage,
    removeMessage,
    updateMessage,
    setState,
    setAuth,
    setError,
    setFileUpload,
    addPendingAttachment,
    clearPendingAttachments,
    requestInputFocus,
    setDrivingMode,
    setWaveLevel: waveform.setWaveLevel,
    resetWaveform: waveform.resetWaveform,
  });

  const rateLimitGate = useRateLimitGate({
    rateLimitLock,
    setState,
    requestInputFocus,
    addAssistantMessage: actions.addAssistantMessage,
  });

  useEffect(() => {
    if (shouldFocus) inputRef.current?.focus();
  }, [shouldFocus]);

  useEffect(() => {
    if (inputFocusPending) {
      consumeInputFocus();
      inputRef.current?.focus();
    }
  }, [consumeInputFocus, inputFocusPending]);

  if (rateLimitLock && rateLimitGate.remaining > 0) {
    return <RateLimitCountdownView remaining={rateLimitGate.remaining} />;
  }

  if (rateLimitLock?.captchaRequired) {
    if (rateLimitLock.captchaQuestion) {
      return (
        <CaptchaChallengeView
          question={rateLimitLock.captchaQuestion}
          captchaInputRef={rateLimitGate.captchaInputRef}
          onSubmit={rateLimitGate.handleCaptchaSubmit}
        />
      );
    }

    return <CaptchaLoadingView />;
  }

  if (useSso && auth.status === 'unauthenticated') {
    return (
      <SsoLoginView
        provider={config.authProvider}
        onLogin={actions.handleSsoLogin}
      />
    );
  }

  if (useSso && auth.status === 'sso_pending') {
    return <SsoPendingView />;
  }

  return (
    <ChatComposer
      fileUpload={fileUpload}
      pendingAttachments={pendingAttachments}
      removePendingAttachment={removePendingAttachment}
      showAttach={showAttach}
      showMic={showMic}
      showDrivingMode={showDrivingMode}
      isLoading={isLoading}
      isAuthenticated={isAuthenticated}
      authStatus={auth.status}
      inputDraft={actions.inputDraft}
      inputRef={inputRef}
      drivingListening={drivingListening}
      waveBarsContainerRef={waveform.waveBarsContainerRef}
      onAttachClick={actions.handleAttachClick}
      onMicClick={actions.handleMicClick}
      onDrivingModeClick={handleDrivingModeClick}
      onTextareaInput={actions.handleTextareaInput}
      onInputChange={actions.handleInputChange}
      onKeyDown={actions.handleKeyDown}
      onSubmit={actions.handleSubmit}
      onStop={actions.handleStop}
    />
  );
}
